import { CustomFab } from "components/buttons/CustomFab";
import { postData } from "utils/networking/postData";
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Modal,
  TextField,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Paper,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import CancelIcon from "@mui/icons-material/Cancel";
import { useUserType } from "hooks/useUserType";
import { toast } from "react-toastify";
import TableContainer from "@mui/material/TableContainer";
import { USER_TYPES } from "consts";
import ConfirmDialog from "components/ConfirmDialog";


//~interface ApiResponse {
//  success: boolean;
//  message?: string;
//}

//~interface TableRowData {
//  id: string;
//  [key: string]: any;
//}

///////////////////////////////////////////////////////// FORM ///////////////////////////////////////////////////////////////////

interface FieldDef {
  key: string;           // key used in formData
  label: string;         // label shown to user (can have spaces)
  placeholder?: string;  // optional placeholder guide
  defaultValue?: string; // optional default value
}

export const addEntryQuery = async (
  data: Record<string, unknown>,
) => {
  console.log("DATA TO SUBMIT: ", data);
  try {
    const SUBMISSION_URI = "ezyvet/addclient";
    const result = await postData({
      uri: SUBMISSION_URI,
      body: data, 
    });
    console.log("RESULT: ", result);
    if("success" in result) {
      toast.success("Client added");
    } else if(result.errorMessage) {
      toast.error(result.errorMessage);
    }
    return result;
  } catch (error) {
    toast.error(error.errorMessage);
    throw error;
  }
};

export const deleteEntryQuery = async (
  data: Record<string, unknown>,
) => {
  if(true) {
//RABBIT
  }
  console.log("DATA TO SUBMIT: ", data);
  try {
    const SUBMISSION_URI = "ezyvet/removeclient";
    const result = await postData({
      uri: SUBMISSION_URI,
      body: data, 
    });
    console.log("DELETION RESULT: ", result);
    if("success" in result) {
      toast.success("Client Removed");
    } else if(result.errorMessage) {
      toast.error(result.errorMessage);
    }
    return result;
  } catch (error) {
    toast.error(error.errorMessage);
    throw error;
  }
};

///////////////////////////////////////////////////////// TABLE ///////////////////////////////////////////////////////////////////

export const clientDataRetrievalQuery = async (
  data: Record<string, unknown>, //PROBABLY UNNECESSARY
) => {
  //RABBIT - TESTING ONLY
  //const resultx =
  //{
  //  data: [
  //    {pk: 1, clientId: 'atpublic', practice_name: 'prac', feedledee: 'diddlydum', etcetc: 'egasdfasf', whatisthis: 'stuff', anothercolumn: 'yep'},
  //    {pk: 2, clientId: 'ddodo', practice_name: 'attg', feedledee: 'diddlydoo', etcetc: 'egasdfasf', whatisthis: 'dunno', anothercolumn: 'uhha'}
  //  ]
  //};
  //return resultx;
  //END OF TESTING
  if(true) {
//RABBIT
  }
  
  try {
    const SUBMISSION_URI = "ezyvet/getclients";
    const result = await postData({
      uri: SUBMISSION_URI,
      body: data, //PROBABLY UNNECESSARY
    });
    //console.log("CLIENT DATA RETRIEVAL RESULT: ", result);
    if("success" in result) {
      //toast.success("Success");
    } else if(result.errorMessage) {
      toast.error(result.errorMessage);
    }
    return result;
  } catch (error) {
    toast.error(error.errorMessage);
    throw error;
  }
};

///////////////////////////////////////////////////////// MODAL ELEMENT (COMBINED) ///////////////////////////////////////////////////////////////////

export const EzyvetDataEntry: React.FC = () => {
  type ConfirmDialogAction = "delete" | "save" | null;
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [confirmDialogAction, setConfirmDialogAction] = useState<ConfirmDialogAction>(null);
  const [confirmDialogSelectedRowIndex, setConfirmDialogSelectedRowIndex] = useState<number | null>(null);
  const [confirmDialogOkToDelete, setConfirmDialogOkToDelete] = useState(false);
  const [confirmDialogOkToSave, setConfirmDialogOkToSave] = useState(false);
  const fieldDefs: FieldDef[] = [
    { key: "fieldPracticeName", label: "Practice Name", placeholder: "Name of Practice as per PACS" },
    { key: "fieldAETitle", label: "AE Title", placeholder: "Ensure this is hard to guess" },
    { key: "fieldModality", label: "Modality", placeholder: "eg DX" },
    { key: "fieldClientAbbreviation", label: "Practice Abbreviation", placeholder: "Short unique identifier for the practice" },
    { key: "fieldStationName", label: "Station Name", placeholder: "As per client imaging software" },
    { key: "fieldClientId", label: "Client ID", placeholder: "Obtain from client (step 2)" },
    { key: "fieldClientSecret", label: "Client Secret", placeholder: "Obtain from client (step 2)" },
    { key: "fieldGrantType", label: "Grant Type", defaultValue: "client_credentials" },
    { key: "fieldScope", label: "Scope", defaultValue: "read-diagnosticrequest,read-diagnosticresult,read-diagnosticresultitem,read-systemsetting,write-attachment,write-diagnosticresult,write-diagnosticresultitem" },
    { key: "fieldPartnerId", label: "Partner ID", defaultValue: process.env.REACT_APP_EZYVET_PARTNER_ID },
  ];
  const [formData, setFormData] = useState<Record<string, string>>(
    fieldDefs.reduce((acc, f) => {
      acc[f.key] = f.defaultValue ?? "";
      return acc;
    }, {} as Record<string, string>)
  );

  const modalRef = useRef<HTMLDivElement>(null);

  // Close modal if clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        setModalOpen(false);
      }
    };
    if (modalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [modalOpen]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmitNewClient = async () => {
    console.log("FORM DATA: ", formData);
    const data = {
      practiceName: formData.fieldPracticeName,
      aeTitle: formData.fieldAETitle,
      modality: formData.fieldModality,
      clientAbbreviation: formData.fieldClientAbbreviation,
      stationName:  formData.fieldStationName,
      clientId: formData.fieldClientId,
      clientSecret: formData.fieldClientSecret,
      grantType: formData.fieldGrantType,
      scope: formData.fieldScope,
      partnerId: formData.fieldPartnerId,
    };
    const result = await addEntryQuery(data);
  };

  const handleDeleteNewClient = async () => {
    console.log("FORM DATA: ", formData);
    const data = {
      practiceName: formData.fieldPracticeName,
      aeTitle: formData.fieldAETitle,
    };
    const result = await deleteEntryQuery(data);
  };

  const { userType, userTypeColour } = useUserType();

  const svgContent = `
    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1600 900'>
      <path d='M450,-20 C300,200 100,200 -20,250' fill='none' stroke='${userTypeColour}' stroke-width='25' opacity='0.7'/>
      <path d='M250,-20 Q200,350 -20,400' fill='none' stroke='${userTypeColour}' stroke-width='25' opacity='0.3'/>
      <path d='M1620,200 C1200,300 1550,700 1000,920' fill='none' stroke='${userTypeColour}' stroke-width='25' opacity='0.7'/>
      <path d='M1620,450 Q1050,450 750,920' fill='none' stroke='${userTypeColour}' stroke-width='25' opacity='0.7'/>
      <path d='M1250,-20 Q1300,200 1620,250' fill='none' stroke='${userTypeColour}' stroke-width='25' opacity='0.3'/>
    </svg>
    `;
  const backgroundRibbonsImage = `url("data:image/svg+xml,${encodeURIComponent(svgContent)}")`;

  const handleKeyCloakClick = () => {
    window.open('https://keyserver8192839.herokuapp.com/auth/admin/master/console/#/realms/ezyvet-production/clients', '_blank');
  }

  const handleUserGuideClick = () => {
    window.open('https://storage.cloud.google.com/atx_documents/ATX%20ezyVet%20User%20Guide_u7eo0ar4qp2e8m4f33jf.pdf','_blank');//'https://storage.googleapis.com/atx_documents/ATX%20ezyVet%20User%20Guide_u7eo0ar4qp2e8m4f33jf.pdf', '_blank')
  }

  const handleGoogleLogsClick = () => {
    window.open('https://console.cloud.google.com/logs/viewer?advancedFilter=resource.type%20%3D%20%22cloud_run_revision%22%0Aresource.labels.service_name%20%3D%20%22production-atx-imaging%22%0Aresource.labels.location%20%3D%20%22australia-southeast1%22%0A%20severity%3E%3DDEFAULT&project=atxpacs', '_blank');
  }

  const handleATXGuideClick = () => {
    window.open('https://storage.cloud.google.com/atx_documents/Configuring%20new%20clients%20to%20ATX%20Ezyvet%20integration_avpwfxi4zxzjb62pczph.pdf', '_blank');
  }
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editRow, setEditRow] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});

  // Load data from backend
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const result: any = await clientDataRetrievalQuery({ someParam: "value" });
      if (result?.data) {
        setRows(result.data);
      }
      setLoading(false);
    };
    fetchData();
  }, [modalOpen]);

  // Escape key exits edit mode without saving
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setEditRow(null);
        setEditValues({});
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleTableEditClick = (rowIndex: number) => {
    setEditRow(rowIndex);
    setEditValues({ ...rows[rowIndex] });
  };

  const handleTableSaveClick = async (rowIndex: number) => {
    setConfirmDialogSelectedRowIndex(rowIndex);
    setConfirmDialogAction("save");
    setConfirmationDialogOpen(true);
  };

  const handleTableSave = async (rowIndex: number) => {
    console.log("VALUES TO BE SAVED: ", editValues); //Object with column:value key-vaue pairs for updated row
    console.log("OR: ", rows[rowIndex]);

    // Call backend update API here
    //await postData({
    //  uri: "update-client",
    //  body: editValues,
    //});
    try {
      const SUBMISSION_URI = "ezyvet/updateclient";
      const result = await postData({
        uri: SUBMISSION_URI,
        body: editValues,//rows[rowIndex], 
      });
      console.log("UPDATE RESULT: ", result);
      if("success" in result) {
        //setRows((prev) => prev.filter((_, idx) => idx !== rowIndex));
        //setEditRow(null);
        const updatedRows = [...rows];
        updatedRows[rowIndex] = { ...editValues };
        setRows(updatedRows);
        setEditRow(null);
        toast.success("Client Updated");
      } else if(result.errorMessage) {
        toast.error(result.errorMessage);
      }
        return result;
    } catch (error) {
      toast.error(error.errorMessage);
      throw error;
    }
  };

  const handleTableCancelClick = () => {
    setEditRow(null);
    setEditValues({});
  };

  const handleTableDeleteClick = async (rowIndex: number) => {
    setConfirmDialogSelectedRowIndex(rowIndex);
    setConfirmDialogAction("delete");
    setConfirmationDialogOpen(true);
  };

  const handleTableDelete = async (rowIndex: number) => {
    console.log("ROW TO BE DELETED: ", rows[rowIndex]);
    //return;

    try {
      const SUBMISSION_URI = "ezyvet/removeclient";
      const result = await postData({
        uri: SUBMISSION_URI,
        body: rows[rowIndex], 
      });
      console.log("DELETION RESULT: ", result);
      if("success" in result) {
        setRows((prev) => prev.filter((_, idx) => idx !== rowIndex));
        setEditRow(null);
        toast.success("Client Removed");
      } else if(result.errorMessage) {
        toast.error(result.errorMessage);
      }
        return result;
    } catch (error) {
      toast.error(error.errorMessage);
      throw error;
    }
  };


  const handleTableChange = (field: string, value: string) => {
    setEditValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleConfirmDialog = () => {
    if (confirmDialogSelectedRowIndex === null || confirmDialogAction === null) return;

    if (confirmDialogAction === "delete") {
      handleTableDelete(confirmDialogSelectedRowIndex);
    } else if (confirmDialogAction === "save") {
      handleTableSave(confirmDialogSelectedRowIndex);
    }

    // Reset state
    setConfirmDialogSelectedRowIndex(null);
    setConfirmDialogAction(null);
    setConfirmationDialogOpen(false);
  };

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  return (
    <>
      <CustomFab ariaLabel="open-popup"  
        onClick={() => setModalOpen(true)}
        sx={{
            background: userTypeColour,
            color: "white",
            "&:hover": {
            color: userTypeColour,
            },
        }}
      >
      <Typography variant="button">Ezyvet Setup</Typography>
      </CustomFab>
      {userType === USER_TYPES.ADMIN || userType === USER_TYPES.DEVELOPER ? 
        <Modal 
          open={modalOpen} 
          onClose={() => setModalOpen(false)}
        >
          <Box
            ref={modalRef}
            sx={{            
              position: "absolute" as const,
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              backgroundImage: backgroundRibbonsImage,
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover", // or 'contain'
              backgroundPosition: "center",
              boxShadow: 24,
              p: 3,
              display: "flex",
              gap: 3,
              width: "90%",
              maxHeight: "80vh",
              overflowY: "auto",
              borderRadius: 2,
            }}
          >
            {/* Left section - Data entry */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="h4" sx={{color: userTypeColour, textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }} >Add Client / Modality</Typography>
              {fieldDefs.map(({ key, label, placeholder }) => ( //{Object.keys(formData).map((key, label, placeholderfield, defaultValue) => (
                <TextField
                  key={key}
                  label={label}
                  placeholder={placeholder}
                  value={formData[key]}
                  onChange={(e) => handleChange(key, e.target.value)}
                  helperText={
                    key === "none"//"fieldPracticeName" || key === "fieldAETitle" || key === "fieldModality"
                      ? "(also used to identify client for deletion)"
                      : ""
                  }
                  sx={
                    key === "none"//"fieldPracticeName" || key === "fieldAETitle" || key === "fieldModality"
                      ? {
                          //border: "2px solid red",
                          //borderRadius: 1,
                          //backgroundColor: "#fff5f5",
                        }
                      : {}
                  }
                />
              ))}
              <Box sx={{ display: "flex", gap: 1, paddingBottom: '20px' }}>
                <Button variant="contained" onClick={handleSubmitNewClient} sx={{color: 'white', backgroundColor: userTypeColour}}>
                  Add Client / Modality
                </Button>
                {/*<Button variant="outlined" color="error" onClick={handleDeleteNewClient}>
                  Delete Client
                </Button>*/}
              </Box>
            </Box>

            {/* Middle section - Instructions */}
            <Box sx={{ 
              flex: 1,//flex: 0.8, 
              height: '100%',
              width: '100%',
              //bgcolor: userTypeColour, 
              border: `5px solid ${userTypeColour}`,
              p: 2, 
              borderRadius: 2,
            }}>
              <Typography variant="h4" sx={{padding: '5px', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>EzyVet Instructions</Typography>
              <Typography variant="h6" sx={{padding: '5px'}}>1. Set up client in PACS</Typography>
              <Typography variant="body2" sx={{padding: '5px'}}>Set up the client in PACS if not done so already. Ensure the ae title is hard to guess.</Typography>
              <Typography variant="h6" sx={{padding: '5px'}}>2. Receive credentials from client</Typography>
              <Typography variant="body2" sx={{padding: '5px'}}>
                Client to follow procedure:
                <Button onClick={handleUserGuideClick} style={{ boxShadow: `0px 2px 4px rgba(0, 0, 0, 0.4)`, color: 'white', backgroundColor: userTypeColour, marginLeft: '5px'}}>User Guide</Button>
              </Typography>
              <Typography variant="h6" sx={{padding: '5px'}}>3. Add fields to ATX database</Typography>
              <Typography variant="body2" sx={{padding: '5px'}}>Fill in the "Add Client / Modality" section then click "Add Client / Modality".</Typography>
              <Typography variant="h6" sx={{padding: '5px'}}>4. Add client into Keycloak</Typography>
              <Typography variant="body2" sx={{padding: '5px'}}>
                As per ATX guide step 4:
                <Button onClick={handleKeyCloakClick} style={{ boxShadow: `0px 2px 4px rgba(0, 0, 0, 0.4)`, color: 'white', backgroundColor: userTypeColour, marginLeft: '5px'}}>KeyCloak</Button>
              </Typography>
              <Typography variant="h6" sx={{padding: '5px'}}>5. Send id and secret to client</Typography>
              <Typography variant="body2" sx={{padding: '5px'}}>Email the values stored within Keycloak to the client.</Typography>
              <Typography variant="h6" sx={{padding: '5px'}}>6. Set up aquisition software</Typography>
              <Typography variant="body2" sx={{padding: '5px'}}>Ensure the software can read the modality worklist.</Typography>
              <Typography variant="h4" sx={{padding: '5px', paddingTop: '20px'}}>Resources</Typography>
              <Button onClick={handleGoogleLogsClick} style={{ width: '140px', boxShadow: `0px 2px 4px rgba(0, 0, 0, 0.4)`, color: 'white', backgroundColor: userTypeColour, marginLeft: '10px', marginRight: '10px' }}>
                Google Logs
              </Button>
              <Button onClick={handleATXGuideClick} style={{ width: '140px', boxShadow: `0px 2px 4px rgba(0, 0, 0, 0.4)`, color: 'white', backgroundColor: userTypeColour, marginLeft: '10px', marginRight: '10px' }}>
                ATX Guide
              </Button>
            </Box>

            {/* Right section - Table viewer ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~*/}
            <Box sx={{ flex: 1.5, display: "flex", flexDirection: "column", height: "124vh" }}>
              <Typography variant="h4" sx={{color: userTypeColour, textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>Client List</Typography>
              {/*<Paper sx={{ maxHeight: "100%", width: 550, overflow: "auto" }}>*/}

                {loading ? (
                  <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                    <CircularProgress />
                  </Box>
                ) : (                
                  <TableContainer component={Paper} 
                    sx={{
                      maxHeight: "100%",
                      width: 550,
                      overflowX: "scroll", // only one scrollbar
                    }}
                  >
                    <Table stickyHeader size="small" sx={{ minWidth: 600 }}>
                      <TableHead>
                        <TableRow>
                          {rows.length > 0 &&
                            Object.keys(rows[0]).map((col) => (
                              <TableCell key={col} sx={{ fontWeight: "bold" }}>{col}</TableCell>
                            ))}
                          <TableCell sx={{ fontWeight: "bold" }}>Edit</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row, rowIndex) => (
                          <TableRow key={rowIndex}>
                            {Object.keys(row).map((field, colIndex) => (
                              <TableCell key={field}
                              sx={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              >
                                {editRow === rowIndex && colIndex !== 0 && colIndex !== 1 ? ( // colIndex 0 = first column (read-only)
                                  <TextField
                                    value={editValues[field] ?? ""}
                                    onChange={(e) =>
                                      handleTableChange(field, e.target.value)
                                    }
                                    variant="standard"
                                  />
                                ) : (
                                  row[field]
                                )}
                              </TableCell>
                            ))}
                            <TableCell>
                              {editRow === rowIndex ? (
                                <>
                                  <Tooltip title="Cancel" placement="right">
                                    <IconButton
                                      size="small"
                                      color="inherit"
                                      onClick={handleTableCancelClick}
                                    >
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Save" placement="right">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleTableSaveClick(rowIndex)}
                                    >
                                      <SaveIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete" placement="right">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleTableDeleteClick(rowIndex)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              ) : (
                                <IconButton
                                  size="small"
                                  onClick={() => handleTableEditClick(rowIndex)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                <ConfirmDialog
                  open={confirmationDialogOpen}
                  message={
                    confirmDialogAction === "delete"
                      ? "Are you sure you want to delete this entry?"
                      : "Are you sure you want to update this entry?"
                  }
                  onConfirm={handleConfirmDialog}
                  onCancel={() => {
                    setConfirmDialogSelectedRowIndex(null);
                    setConfirmDialogAction(null);
                    setConfirmationDialogOpen(false);
                  }}
                  PaperProps={{
                    onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
                  }}
                />

              {/*</Paper>*/}
            </Box>
          </Box>
        </Modal>
      :
        <></>
      }
    </>
  );
};
